import { useMutation, useQuery } from "@apollo/client/react";
import {
  CreateStoreDocument,
  MyStoreDocument,
  PublishStoreDocument,
  UnpublishStoreDocument,
  UpdateStoreDocument,
} from "@/generated/graphql";
import { graphQLErrorMessage } from "@/lib/graphqlErrors";
import type { Store, StoreInput } from "@/types/models";

export function useStoreSettings() {
  const { data, loading, error, refetch } = useQuery(MyStoreDocument, {
    fetchPolicy: "network-only",
  });

  const store: Store | null = data?.myStore ?? null;

  const [createStoreMutation, { loading: creating }] = useMutation(CreateStoreDocument, {
    refetchQueries: [{ query: MyStoreDocument }],
  });

  const [updateStoreMutation, { loading: updating }] = useMutation(UpdateStoreDocument, {
    refetchQueries: [{ query: MyStoreDocument }],
  });

  const [publishStoreMutation, { loading: publishing }] = useMutation(PublishStoreDocument, {
    refetchQueries: [{ query: MyStoreDocument }],
  });

  const [unpublishStoreMutation, { loading: unpublishing }] = useMutation(UnpublishStoreDocument, {
    refetchQueries: [{ query: MyStoreDocument }],
  });

  const createStore = (input: StoreInput, callbacks?: { onCompleted?: () => void; onError?: (message: string) => void }) => {
    createStoreMutation({
      variables: { input },
      onCompleted: () => callbacks?.onCompleted?.(),
      onError: (err) => {
        callbacks?.onError?.(graphQLErrorMessage(err, "Não foi possível criar a loja."));
      },
    });
  };

  const updateStore = (input: StoreInput, callbacks?: { onCompleted?: () => void; onError?: (message: string) => void }) => {
    updateStoreMutation({
      variables: { input },
      onCompleted: () => callbacks?.onCompleted?.(),
      onError: (err) => {
        callbacks?.onError?.(graphQLErrorMessage(err, "Não foi possível atualizar a loja."));
      },
    });
  };

  const publishStore = (callbacks?: { onCompleted?: () => void; onError?: (message: string) => void }) => {
    publishStoreMutation({
      onCompleted: () => callbacks?.onCompleted?.(),
      onError: (err) => {
        callbacks?.onError?.(graphQLErrorMessage(err, "Não foi possível publicar a loja."));
      },
    });
  };

  const unpublishStore = (callbacks?: { onCompleted?: () => void; onError?: (message: string) => void }) => {
    unpublishStoreMutation({
      onCompleted: () => callbacks?.onCompleted?.(),
      onError: (err) => {
        callbacks?.onError?.(graphQLErrorMessage(err, "Não foi possível despublicar a loja."));
      },
    });
  };

  return {
    store,
    loading,
    error,
    refetch,
    createStore,
    updateStore,
    publishStore,
    unpublishStore,
    saving: creating || updating,
    publishing,
    unpublishing,
  };
}
